import { Link } from 'react-router-dom';
import { liveSessions } from '@/mocks/lives';
import { replays } from '@/mocks/replays';
import { formatCount, todayLabel, todayWeekday, toNextTimestamp } from '@/utils/time';
import CountdownTimer from '@/components/feature/CountdownTimer';

export default function Hero() {
  const liveNow = liveSessions.filter((item) => item.status === 'live');
  const upcoming = liveSessions
    .filter((item) => item.status === 'upcoming')
    .sort((a, b) => a.start.localeCompare(b.start));
  const featured = liveNow[0];
  const nextUp = upcoming.slice(0, 2);

  const stats = [
    { label: '今日场次', value: `${liveSessions.length} 场` },
    { label: '正在直播', value: `${liveNow.length} 场` },
    { label: '回放收录', value: `${replays.length} 条` },
  ];

  return (
    <section className="w-full border-b border-background-200 bg-gradient-to-br from-primary-50 via-background-50 to-background-100">
      <div className="container-page grid grid-cols-1 gap-10 py-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 lg:py-16">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-200 bg-background-50 px-3 py-1.5 text-xs font-medium text-primary-800">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500 animate-pulse" />
            {todayLabel()} {todayWeekday()} · 排期实时更新
          </span>

          <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.15] text-foreground-950 md:text-[56px]">
            <span className="block">爆料直播网</span>
            <span className="mt-1 block text-primary-700">今天播什么，一眼看清</span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            想知道爆料直播今天播什么？这里汇总<strong className="font-semibold text-foreground-900">今日直播排期</strong>与
            <strong className="font-semibold text-foreground-900">即将开始场次</strong>，
            <strong className="font-semibold text-foreground-900">直播回放列表</strong>随时补课，
            <strong className="font-semibold text-foreground-900">热门直播推荐</strong>帮你挑重点，
            还能设置<strong className="font-semibold text-foreground-900">直播预告提醒</strong>，不再错过任何一场。
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/schedule"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-schedule-line" aria-hidden="true" />
              </span>
              查看今日完整排期
            </Link>
            <Link
              to="/replay"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-history-line" aria-hidden="true" />
              </span>
              浏览直播回放
            </Link>
          </div>

          <dl className="mt-9 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
            {stats.map((item) => (
              <div key={item.label}>
                <dt className="text-xs text-foreground-500">{item.label}</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <aside className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground-950">
              <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                <i className="ri-record-circle-line" aria-hidden="true" />
              </span>
              正在直播
            </span>
            <span className="text-xs text-foreground-500">共 {liveNow.length} 场</span>
          </div>

          {featured && (
            <article className="mt-4">
              <Link to={`/live/${featured.id}`} className="group relative block overflow-hidden rounded-md bg-background-200">
                <span className="block aspect-[16/10] w-full">
                  <img
                    src={featured.cover}
                    alt={`爆料直播网 - ${featured.title} 直播封面`}
                    title={`${featured.title} 爆料直播 今日直播排期`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />
                <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                  <span className="h-1.5 w-1.5 rounded-full bg-background-50 animate-pulse" />
                  直播中
                </span>
                <div className="absolute inset-x-0 bottom-0 p-3.5">
                  <h2 className="font-heading text-base font-bold leading-snug text-background-50">
                    {featured.title}
                  </h2>
                  <p className="mt-1.5 flex items-center gap-2 text-[11px] text-background-200">
                    <span>{featured.host}</span>
                    <span>·</span>
                    <span>{formatCount(featured.viewers)} 人在看</span>
                    <span>·</span>
                    <span>{featured.start} 开播</span>
                  </p>
                </div>
              </Link>
              <Link
                to={`/live/${featured.id}`}
                className="mt-3 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                进入直播间
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </span>
              </Link>
            </article>
          )}

          <div className="mt-5 border-t border-background-200 pt-4">
            <p className="text-xs font-medium text-foreground-500">稍后开播</p>
            <ul className="mt-3 flex flex-col gap-3">
              {nextUp.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/live/${item.id}`}
                      className="line-clamp-1 font-heading text-[13px] font-semibold text-foreground-900 transition-colors hover:text-primary-700"
                    >
                      {item.title}
                    </Link>
                    <p className="mt-0.5 text-[11px] text-foreground-500">
                      {item.start} · {item.category} · {item.host}
                    </p>
                  </div>
                  <CountdownTimer targetMs={toNextTimestamp(item.start)} />
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}